import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Avatar,
  Stack,
  Chip,
  CircularProgress,
  Tooltip,
  Collapse,
  Divider,
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as AgentIcon,
  Person as UserIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { 
  useChatWithAgentMutation, 
  useGetAgentInfoQuery,
  AgentChatResponse,
  ToolUsageInfo 
} from '../../../services/AppealsService';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  toolsUsed?: ToolUsageInfo[];
  isLoading?: boolean;
}

interface AgentChatProps {
  /** Optional remit ID to provide denial claim context */
  remitId?: string;
  /** Optional zero-based index of the claim within the remit */
  claimIndex?: number;
  /** Optional denial code to provide context */
  denialCode?: string;
  /** Callback when agent generates an appeal letter */
  onAppealGenerated?: (content: string) => void;
  /** Custom height for the chat panel */
  height?: string | number;
}

export const AgentChat: React.FC<AgentChatProps> = ({
  remitId,
  claimIndex,
  denialCode,
  onAppealGenerated,
  height = '600px',
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [expandedTools, setExpandedTools] = useState<Set<string>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const { data: agentInfo } = useGetAgentInfoQuery();
  const [sendMessage, { isLoading }] = useChatWithAgentMutation();
  
  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Add initial greeting
  useEffect(() => {
    if (messages.length === 0) {
      const greeting = getInitialGreeting();
      setMessages([{
        id: 'greeting',
        role: 'assistant',
        content: greeting,
        timestamp: new Date(),
      }]);
    }
  }, [remitId, claimIndex, denialCode]);
  
  const getInitialGreeting = useCallback(() => {
    let greeting = "Hello! I'm the Appeals Agent, here to help you create compelling claim appeal letters. ";
    
    if (remitId || claimIndex !== undefined) {
      greeting += "I can see you're working on a specific denial claim. ";
    }
    
    if (denialCode) {
      greeting += `I notice you have denial code ${denialCode}. I can help you understand this denial and build a strong appeal. `;
    }
    
    greeting += "\n\nHow can I assist you today? You can ask me to:\n";
    greeting += "• Explain a denial code and suggest resolution strategies\n";
    greeting += "• Search for relevant payer policies\n";
    greeting += "• Draft an appeal letter based on the clinical documentation\n";
    greeting += "• Review and improve an existing appeal letter";
    
    return greeting;
  }, [remitId, claimIndex, denialCode]);
  
  const handleSend = async () => {
    if (!inputValue.trim() || isLoading) return;
    
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date(),
    };
    
    const loadingMessage: Message = {
      id: `loading-${Date.now()}`,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isLoading: true,
    };
    
    setMessages(prev => [...prev, userMessage, loadingMessage]);
    setInputValue('');
    
    try {
      const response = await sendMessage({
        message: inputValue.trim(),
        conversationId,
        remitId,
        claimIndex,
        denialCode,
      }).unwrap();
      
      // Update conversation ID for continuity
      if (!conversationId) {
        setConversationId(response.conversationId);
      }
      
      // Replace loading message with actual response
      setMessages(prev => 
        prev.map(msg => 
          msg.isLoading
            ? {
                id: `assistant-${Date.now()}`,
                role: 'assistant' as const,
                content: response.message,
                timestamp: new Date(),
                toolsUsed: response.toolsUsed,
              }
            : msg
        )
      );
      
      // Check if response contains an appeal letter
      if (response.message.includes('Dear') && response.message.includes('Sincerely')) {
        onAppealGenerated?.(response.message);
      }
    } catch (error) {
      // Replace loading with error message
      setMessages(prev => 
        prev.map(msg => 
          msg.isLoading
            ? {
                id: `error-${Date.now()}`,
                role: 'assistant' as const,
                content: 'I apologize, but I encountered an error processing your request. Please try again.',
                timestamp: new Date(),
              }
            : msg
        )
      );
    }
  };
  
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  
  const toggleToolsExpanded = (messageId: string) => {
    setExpandedTools(prev => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  };
  
  const handleCopy = async (content: string, messageId: string) => {
    await navigator.clipboard.writeText(content);
    setCopiedId(messageId);
    setTimeout(() => setCopiedId(null), 2000);
  };
  
  return (
    <Paper 
      elevation={2} 
      sx={{ 
        height, 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
            <AgentIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {agentInfo?.name || 'Appeals Agent'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              AI-powered claim appeal assistance
            </Typography>
          </Box>
        </Stack>
      </Box>
      
      {/* Messages */}
      <Box 
        sx={{ 
          flex: 1, 
          overflow: 'auto', 
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {messages.map((message) => (
          <MessageBubble
            key={message.id}
            message={message}
            isToolsExpanded={expandedTools.has(message.id)}
            onToggleTools={() => toggleToolsExpanded(message.id)}
            onCopy={() => handleCopy(message.content, message.id)}
            isCopied={copiedId === message.id}
          />
        ))}
        <div ref={messagesEndRef} />
      </Box>
      
      {/* Input */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Stack direction="row" spacing={1} alignItems="flex-end">
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            maxRows={4}
            placeholder="Ask about denial codes, payer policies, or request help with an appeal..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
              },
            }}
          />
          <IconButton 
            color="primary" 
            onClick={handleSend}
            disabled={!inputValue.trim() || isLoading}
            sx={{ 
              bgcolor: 'primary.main', 
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' },
              '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
            }}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
          </IconButton>
        </Stack>
      </Box>
    </Paper>
  );
};

// Message Bubble Component
interface MessageBubbleProps {
  message: Message;
  isToolsExpanded: boolean;
  onToggleTools: () => void;
  onCopy: () => void;
  isCopied: boolean;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isToolsExpanded,
  onToggleTools,
  onCopy,
  isCopied,
}) => {
  const isUser = message.role === 'user';
  
  if (message.isLoading) {
    return (
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
          <AgentIcon fontSize="small" />
        </Avatar>
        <Paper 
          sx={{ 
            p: 2, 
            maxWidth: '80%',
            bgcolor: 'grey.100',
            borderRadius: 2,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Thinking...
            </Typography>
          </Stack>
        </Paper>
      </Stack>
    );
  }
  
  return (
    <Stack 
      direction="row" 
      spacing={1} 
      alignItems="flex-start"
      sx={{ 
        flexDirection: isUser ? 'row-reverse' : 'row',
        '& > .MuiStack-root': { alignItems: isUser ? 'flex-end' : 'flex-start' },
      }}
    >
      <Avatar 
        sx={{ 
          bgcolor: isUser ? 'secondary.main' : 'primary.main', 
          width: 32, 
          height: 32 
        }}
      >
        {isUser ? <UserIcon fontSize="small" /> : <AgentIcon fontSize="small" />}
      </Avatar>
      
      <Stack spacing={0.5} sx={{ maxWidth: '80%' }}>
        <Paper 
          sx={{ 
            p: 2, 
            bgcolor: isUser ? 'primary.main' : 'grey.100',
            color: isUser ? 'white' : 'text.primary',
            borderRadius: 2,
          }}
        >
          <Typography 
            variant="body2" 
            sx={{ 
              whiteSpace: 'pre-wrap',
              '& code': {
                bgcolor: isUser ? 'primary.dark' : 'grey.200',
                px: 0.5,
                borderRadius: 0.5,
                fontFamily: 'monospace',
              },
            }}
          >
            {message.content}
          </Typography>
        </Paper>
        
        {/* Tool usage indicator */}
        {message.toolsUsed && message.toolsUsed.length > 0 && (
          <Box>
            <Stack 
              direction="row" 
              spacing={0.5} 
              alignItems="center"
              sx={{ cursor: 'pointer' }}
              onClick={onToggleTools}
            >
              <Typography variant="caption" color="text.secondary">
                Used {message.toolsUsed.length} tool{message.toolsUsed.length > 1 ? 's' : ''}
              </Typography>
              {isToolsExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </Stack>
            
            <Collapse in={isToolsExpanded}>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                {message.toolsUsed.map((tool, idx) => (
                  <Tooltip 
                    key={idx} 
                    title={`${tool.durationMs}ms`}
                  >
                    <Chip
                      size="small"
                      label={tool.name}
                      color={tool.success ? 'success' : 'error'}
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  </Tooltip>
                ))}
              </Stack>
            </Collapse>
          </Box>
        )}
        
        {/* Copy button for assistant messages */}
        {!isUser && (
          <Box>
            <Tooltip title={isCopied ? 'Copied!' : 'Copy message'}>
              <IconButton size="small" onClick={onCopy}>
                {isCopied ? <CheckIcon fontSize="small" color="success" /> : <CopyIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        )}
        
        <Typography variant="caption" color="text.secondary">
          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </Typography>
      </Stack>
    </Stack>
  );
};

export default AgentChat;
